import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const pluginKey = new PluginKey("trailingNode");

/**
 * Guarantees a typable node always exists after the last node in the document, so the cursor is
 * never stranded with nothing to type into after an isolating node (e.g. a table) — isolating
 * nodes block the cursor from being placed directly next to them the way ordinary blocks allow.
 */
export default Extension.create({
  name: "trailingNode",

  addOptions() {
    return {
      node: "paragraph",
    };
  },

  addProseMirrorPlugins() {
    const { node: nodeName } = this.options;

    const lastNodeNeedsTrailingNode = (doc) => {
      const { lastChild } = doc;
      return !!lastChild && !!lastChild.type.spec.isolating;
    };

    const appendTrailingNode = (state) => {
      const nodeType = state.schema.nodes[nodeName];
      return state.tr.insert(state.doc.content.size, nodeType.create());
    };

    return [
      new Plugin({
        key: pluginKey,
        state: {
          init: (_, state) => lastNodeNeedsTrailingNode(state.doc),
          apply: (transaction, value, _oldState, newState) => (
            transaction.docChanged ? lastNodeNeedsTrailingNode(newState.doc) : value
          ),
        },
        appendTransaction: (_transactions, _oldState, newState) => {
          if (!pluginKey.getState(newState)) {
            return null;
          }

          return appendTrailingNode(newState);
        },
        // Content loaded straight into the editor (e.g. saved HTML that already ends in a
        // table) needs fixing up here too — appendTransaction only runs off the back of a
        // dispatched transaction, so it never sees a document that already has the problem
        // when nothing has been edited yet.
        view: (editorView) => {
          if (lastNodeNeedsTrailingNode(editorView.state.doc)) {
            editorView.dispatch(appendTrailingNode(editorView.state));
          }

          return {};
        },
      }),
    ];
  },
});
